/*
  featured-tabs.js
  JavaScript for the "Featured Tabs (Advanced)" section.
  This script is now scoped to work with multiple sections on the same page.
*/

class CustomFeaturedTabs {
  constructor(sectionContainer) {
    // All queries are now scoped to this specific section container
    this.container = sectionContainer;
    this.tablist = this.container.querySelector('[role="tablist"]');
    this.tabs = this.container.querySelectorAll('[role="tab"]');
    this.panels = this.container.querySelectorAll('[role="tabpanel"]');
    this.shopAllLink = this.container.querySelector('[data-shop-all-link]');
    this.shopAllText = this.container.querySelector('.shop-all-text');
    
    this.sliders = new Map();

    if (!this.tablist) return;

    this.initTabs();
    this.initSliders();
  }

  initTabs() {
    const firstTab = this.tabs[0];
    if (firstTab) {
      this.shopAllLink.href = firstTab.dataset.collectionUrl;
    }

    this.tablist.addEventListener('click', (e) => {
      const clickedTab = e.target.closest('button[role="tab"]');
      if (!clickedTab) return;
      this.switchTab(clickedTab);
    });
  }

  switchTab(newTab) {
    const oldTab = this.tablist.querySelector('[aria-selected="true"]');
    // Find the panel *within this section*
    const newPanel = this.container.querySelector(`#${newTab.getAttribute('aria-controls')}`);
    
    if (oldTab) {
      oldTab.setAttribute('aria-selected', 'false');
    }
    this.panels.forEach(panel => panel.setAttribute('hidden', 'true'));

    newTab.setAttribute('aria-selected', 'true');
    newPanel.removeAttribute('hidden');

    this.shopAllLink.href = newTab.dataset.collectionUrl;
    this.shopAllText.innerText = newTab.dataset.collectionName;
    
    // Re-check slider arrows on the newly visible panel
    const sliderInstance = this.sliders.get(newPanel);
    if (sliderInstance) {
      sliderInstance.checkArrows();
    }
  }
  
  initSliders() {
    this.panels.forEach(panel => {
      // Pass the scoped panel element to the slider class
      const slider = new ProductSlider(panel);
      this.sliders.set(panel, slider);
    });
  }
}

class ProductSlider {
  constructor(panel) {
    // All queries are scoped to the panel this slider lives in
    this.wrapper = panel.querySelector('[data-product-slider-wrapper]');
    if (!this.wrapper) return;

    this.grid = panel.querySelector('[data-product-grid]');
    this.dotsContainer = panel.querySelector('[data-slider-dots]');
    this.prevArrow = panel.querySelector('[data-slider-prev]');
    this.nextArrow = panel.querySelector('[data-slider-next]');
    this.items = this.grid.querySelectorAll('.adv-product-card');
    this.productCount = parseInt(this.wrapper.dataset.productCount, 10);
    
    this.isMobile = window.innerWidth < 750;
    this.init();
  }

  init() {
    this.addListeners();
    
    if (this.isMobile) {
      this.initMobileDots();
    } else {
      this.initDesktopArrows();
    }
  }

  addListeners() {
    this.grid.addEventListener('scroll', this.debounce(this.onMobileScroll.bind(this), 100));
    this.prevArrow.addEventListener('click', this.onPrevClick.bind(this));
    this.nextArrow.addEventListener('click', this.onNextClick.bind(this));

    // Re-check layout on resize
    window.addEventListener('resize', this.debounce(() => {
      const newIsMobile = window.innerWidth < 750;
      if (newIsMobile !== this.isMobile) {
        this.isMobile = newIsMobile;
        // Re-initialize dots or arrows based on new screen size
        if (this.isMobile) {
          this.initMobileDots();
          this.wrapper.classList.remove('slider-desktop--active');
        } else {
          this.initDesktopArrows();
          this.dotsContainer.innerHTML = ''; // Clear mobile dots
        }
      }
      this.checkArrows(); // Always check arrows on resize
    }, 200));
  }

  initMobileDots() {
    if (this.productCount <= 2) {
      this.dotsContainer.hidden = true;
      return;
    }
    
    this.dotsContainer.hidden = false;
    const pageCount = Math.ceil(this.productCount / 2);
    
    // Only build dots if they don't exist or are wrong
    if (this.dotsContainer.children.length !== pageCount) {
      this.dotsContainer.innerHTML = ''; // Clear existing dots
      
      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'adv-slider-dots__dot';
        dot.dataset.index = i;
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        if (i === 0) dot.classList.add('is-active');
        
        dot.addEventListener('click', () => {
          const itemIndex = i * 2;
          if (this.items[itemIndex]) {
            this.grid.scrollTo({
              left: this.items[itemIndex].offsetLeft - parseInt(getComputedStyle(this.grid).paddingLeft),
              behavior: 'smooth'
            });
          }
        });
        this.dotsContainer.appendChild(dot);
      }
    }
    this.checkMobileDots();
  }
  
  onMobileScroll() {
    if (window.innerWidth >= 750) return;
    this.checkMobileDots();
  }
  
  checkMobileDots() {
    if (this.productCount <= 2 || !this.items.length) return;
    
    const scrollLeft = this.grid.scrollLeft;
    // Get item width from the first item
    const itemWidth = this.items[0].offsetWidth + parseInt(getComputedStyle(this.grid).gap);
    const currentPage = Math.round((scrollLeft / (itemWidth * 2)));

    this.dotsContainer.querySelectorAll('.adv-slider-dots__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === currentPage);
    });
  }

  initDesktopArrows() {
    if (this.productCount > 4) {
      this.wrapper.classList.add('slider-desktop--active');
      this.checkArrows();
    } else {
      this.wrapper.classList.remove('slider-desktop--active');
    }
  }
  
  onPrevClick() {
    const scrollAmount = this.grid.clientWidth - parseInt(getComputedStyle(this.grid).gap);
    this.grid.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    setTimeout(() => this.checkArrows(), 400);
  }
  
  onNextClick() {
    const scrollAmount = this.grid.clientWidth - parseInt(getComputedStyle(this.grid).gap);
    this.grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(() => this.checkArrows(), 400);
  }

  checkArrows() {
    if (window.innerWidth < 750) return;
    if (this.productCount <= 4) return;
    
    const scrollLeft = Math.ceil(this.grid.scrollLeft);
    const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
    
    this.prevArrow.disabled = scrollLeft <= 1;
    this.nextArrow.disabled = scrollLeft >= (maxScroll - 1);
  }
  
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// --- NEW INITIALIZATION CODE ---
// This part runs once the document is loaded.

const initFeaturedTabs = () => {
  // 1. Find all sections on the page that match our section's class
  const sections = document.querySelectorAll('.adv-tabs-section[data-section-id]');
  
  // 2. Loop over each one and create a new, independent instance
  sections.forEach(section => {
    // We add a class to prevent initializing the same section twice
    if (!section.classList.contains('js-tabs-initialized')) {
      section.classList.add('js-tabs-initialized');
      new CustomFeaturedTabs(section);
    }
  });
}

// This handles the case where the script is loaded with 'defer'
// and 'DOMContentLoaded' might have already fired.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturedTabs);
} else {
  // DOM is already loaded
  initFeaturedTabs();
}